<template>
  <div class="app-shell">
    <Menubar v-if="showShell" :model="navItems" class="app-nav">
      <template #start>
        <RouterLink to="/" class="app-brand">
          <span class="brand-icon">🎌</span>
          <span class="brand-name">Ahegao</span>
        </RouterLink>
      </template>
      <template #end>
        <RouterLink
          v-if="tasksStore.runningCount > 0"
          to="/tasks"
          class="tasks-chip"
          :title="`${tasksStore.runningCount} running task${tasksStore.runningCount === 1 ? '' : 's'}`"
        >
          <i class="pi pi-spin pi-spinner" />
          <span>{{ tasksStore.runningCount }}</span>
        </RouterLink>
        <span
          class="ws-dot"
          :class="{ on: tasksStore.connected }"
          :title="tasksStore.connected ? 'WebSocket connected' : 'WebSocket disconnected — reconnecting'"
        />
        <Button
          icon="pi pi-sign-out"
          severity="secondary"
          text
          rounded
          aria-label="Sign out"
          @click="onLogout"
        />
        <Tag
          :value="isDev ? 'DEV' : 'PROD'"
          :severity="isDev ? 'warn' : 'success'"
          class="env-tag"
        />
      </template>
    </Menubar>

    <main class="app-main" :class="{ bare: !showShell }">
      <!-- keyed by route: Collection and Discover are one view, two galleries -->
      <RouterView :key="String(route.name)" />
    </main>

    <Toast />

    <!-- Deleted: one tap takes it back -->
    <Toast :group="UNDO_GROUP" position="bottom-center">
      <template #message="{ message }">
        <div class="undo-toast">
          <div>
            <div class="undo-summary">{{ message.summary }}</div>
            <div class="undo-detail">{{ message.detail }}</div>
          </div>
          <Button v-if="message.data?.javId" label="Undo" icon="pi pi-undo" size="small" @click="undo(message)" />
        </div>
      </template>
    </Toast>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import Menubar from 'primevue/menubar'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import type { ToastMessageOptions } from 'primevue/toast'
import { UNDO_GROUP, useDeleteVideo } from '@/composables/useDeleteVideo'
import { useAppStore } from '@/stores/app'
import { useTasksStore } from '@/stores/tasks'

const isDev = import.meta.env.DEV

const router = useRouter()
const route = useRoute()
const app = useAppStore()
const tasksStore = useTasksStore()
const toast = useToast()
const { restoreVideo } = useDeleteVideo()

async function undo(message: ToastMessageOptions & { data?: { javId: string } }) {
  toast.remove(message)
  if (message.data?.javId) await restoreVideo(message.data.javId)
}

// The shell (nav + chips) is only meaningful for an authenticated, configured
// app. Login + first-run /settings render bare.
const showShell = computed(() => app.configured === true && app.authenticated === true)

// Persistent WebSocket: when the user is authenticated, keep the tasks store
// connected for the whole session. On logout / cookie expiry / first-run
// redirect, tear down so we don't keep a zombie subscription.
watch(
  () => app.authenticated,
  (authed) => {
    if (authed) {
      tasksStore.start()
    } else {
      tasksStore.stop()
    }
  },
  { immediate: true },
)

const navItems = [
  { label: 'Collection', icon: 'pi pi-video',      command: () => router.push('/') },
  { label: 'Discover',  icon: 'pi pi-globe',       command: () => router.push('/discover') },
  { label: 'Triage',    icon: 'pi pi-filter',      command: () => router.push('/triage') },
  { label: 'Taste',     icon: 'pi pi-heart',       command: () => router.push('/taste') },
  { label: 'Actresses', icon: 'pi pi-users',       command: () => router.push('/actresses') },
  { label: 'Faces',     icon: 'pi pi-face-smile',  command: () => router.push('/faces') },
  { label: 'Tags',      icon: 'pi pi-tag',         command: () => router.push('/tags') },
  { label: 'Organize',  icon: 'pi pi-compass',     command: () => router.push('/organize') },
  { label: 'Downloads', icon: 'pi pi-download',    command: () => router.push('/downloads') },
  { label: 'Trash',     icon: 'pi pi-trash',       command: () => router.push('/trash') },
  { label: 'Tasks',     icon: 'pi pi-list-check',  command: () => router.push('/tasks') },
  { label: 'Libraries', icon: 'pi pi-folder-open', command: () => router.push('/library') },
  { label: 'Settings',  icon: 'pi pi-cog',         command: () => router.push('/settings') },
]

async function onLogout() {
  await app.logout()
  router.replace({ name: 'login' })
}
</script>

<style>
.undo-toast {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}
.undo-toast > div {
  flex: 1;
}
.undo-summary {
  font-weight: 600;
}
.undo-detail {
  font-size: 0.85rem;
  opacity: 0.8;
}

/* ── Reset / base ──────────────────────────────────────────────────── */
*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  font-family: var(--p-font-family, system-ui, sans-serif);
  background: var(--p-surface-ground, #f8f9fa);
  color: var(--p-text-color, #212529);
}

a {
  text-decoration: none;
  color: inherit;
}

/* ── Shell layout ─────────────────────────────────────────────────── */
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-nav {
  position: sticky;
  top: 0;
  z-index: 1000;
  border-radius: 0 !important;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
}

.app-main {
  flex: 1;
  padding: 1.5rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
}

/* On the login / first-run screens we drop padding so the centered card
   gets the full viewport. */
.app-main.bare {
  max-width: none;
  padding: 0;
}

/* ── Brand ────────────────────────────────────────────────────────── */
.app-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  padding: 0 0.5rem;
}

.brand-icon {
  font-size: 1.4rem;
  line-height: 1;
}

.brand-name {
  letter-spacing: 0.03em;
}

/* ── End-of-menubar widgets ──────────────────────────────────────── */
.tasks-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: var(--p-primary-50, #eff6ff);
  color: var(--p-primary-700, #1d4ed8);
  font-size: 0.85rem;
  font-weight: 600;
  margin-right: 0.4rem;
  transition: background 120ms ease;
}
.tasks-chip:hover {
  background: var(--p-primary-100, #dbeafe);
}

.ws-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--p-red-500, #ef4444);
  margin-right: 0.5rem;
  box-shadow: 0 0 0 2px var(--p-surface-0, #fff);
}
.ws-dot.on {
  background: var(--p-green-500, #22c55e);
}

.env-tag {
  font-size: 0.7rem;
  padding: 0.15rem 0.45rem;
  letter-spacing: 0.06em;
  opacity: 0.85;
  margin-left: 0.4rem;
}
</style>
